import {
  IWebsiteBuilderButtonSettings,
  TWebsiteBuilderButtonBorderStyle,
  TWebsiteBuilderButtonIconPosition,
  TWebsiteBuilderButtonType,
  WEBSITE_BUILDER_BUTTON_MAX_BORDER_RADIUS,
  WEBSITE_BUILDER_BUTTON_MAX_FONT_SIZE,
  WEBSITE_BUILDER_BUTTON_MIN_BORDER_RADIUS,
  WEBSITE_BUILDER_BUTTON_MIN_FONT_SIZE,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderButton";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "components/form/input/Input";
import { cloneDeep } from "lodash";
import Toggle from "components/form/toggle/Toggle";
import { CustomColorPickerPopover } from "components/colorpicker";
import { Select } from "components/select/index";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  IWebsiteBuilderSelectOption,
  renderWebsiteBuilderSelectOptions,
} from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";
import { WebsiteBuilderHoverSettings } from "../../../../../helpers/WebsiteBuilderHoverSettings";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { WebsiteBuilderNumberInput } from "pages/secure/facility/websiteBuilder/editor/components/numberInput/WebsiteBuilderNumberInput";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import { ButtonNew as Button } from "components/buttonNew/index";
import useModal from "hooks/modals/useModal";
import { WebsiteBuilderSelectIcon } from "../../../../../helpers/WebsiteBuilderSelectIcon";
import { WebsiteBuilderOptionalLinkSettings } from "../../../../../helpers/WebsiteBuilderOptionalLinkSettings";
import classNames from "classnames";
import { websiteBuilderFindIconAsset } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";

interface IButtonSettingsProps {
  id: string;
  settings: IWebsiteBuilderButtonSettings;
  closeSettings: () => void;
}

export function ButtonSettings(props: IButtonSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { Option } = Select;
  
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderButtonSettings>(() => cloneDeep(settings));

  const {
    state: selectIconModalState,
    updateModal: updateSelectIconModal,
    closeModal: closeSelectIconModal,
  } = useModal();

  const iconAsset = websiteBuilderFindIconAsset(iconAssets, updatedSettings.icon?.assetIdentity);

  const typeOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderButtonType>[] = useMemo(() => {
    return [
      {
        label: t("Primary"),
        value: "primary",
      },
      {
        label: t("Secondary"),
        value: "secondary",
      },
    ];
  }, []);

  const borderStyleOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderButtonBorderStyle>[] = useMemo(() => {
    return [
      {
        label: t("Solid"),
        value: "solid",
      },
      {
        label: t("Transparent"),
        value: "transparent",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderButtonSettings, nextSettings: IWebsiteBuilderButtonSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "button",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "button",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function handleOpenSelectIconModal() {
    updateSelectIconModal({ isOpen: true });
  }

  function handleCloseSelectIconModal() {
    closeSelectIconModal();
  }

  function getFontSizeLabel() {
    if (deviceView === "desktop") {
      return t("Desktop size (px)");
    } else if (deviceView === "tablet") {
      return t("Tablet size (px)");
    } else if (deviceView === "mobile") {
      return t("Mobile size (px)");
    } else {
      return "";
    }
  }

  function renderGeneralSettings() {
    return (
      <>
        <Select
          label={t("Type")}
          defaultValue={updatedSettings.type}
          onChange={(type: TWebsiteBuilderButtonType) =>
            handleUpdateState(prev => ({
              ...prev,
              type,
            }))
          }
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(typeOptions)}
        </Select>
        <Input
          label={t("Label")}
          value={updatedSettings.label}
          onChange={e =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              label: e.target.value,
            }))
          }
        />
        <WebsiteBuilderNumberInput
          label={getFontSizeLabel()}
          initialValue={updatedSettings.fontSize[deviceView]}
          min={WEBSITE_BUILDER_BUTTON_MIN_FONT_SIZE}
          max={WEBSITE_BUILDER_BUTTON_MAX_FONT_SIZE}
          required={false}
          onChange={fontSize => {
            handleDebouncedUpdateState(prev => ({
              ...prev,
              fontSize: {
                ...prev.fontSize,
                [deviceView]: fontSize,
              },
            }));
          }}
        />
        <Select
          label={t("Border Style")}
          defaultValue={updatedSettings.borderStyle}
          onChange={(borderStyle: TWebsiteBuilderButtonBorderStyle) =>
            handleUpdateState(prev => ({
              ...prev,
              borderStyle,
            }))
          }
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(borderStyleOptions)}
        </Select>
        <WebsiteBuilderRange
          label={t("Border radius (px)")}
          min={WEBSITE_BUILDER_BUTTON_MIN_BORDER_RADIUS}
          max={WEBSITE_BUILDER_BUTTON_MAX_BORDER_RADIUS}
          step={1}
          value={updatedSettings.borderRadius}
          setValue={borderRadius =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              borderRadius,
            }))
          }
        />
      </>
    );
  }

  function renderIconSettings() {
    return (
      <>
        <div className="website-builder-button-settings-icon">
          <div
            className={classNames("button-icon-container", {
              "no-icon-border": iconAsset == null,
            })}
          >
            {iconAsset != null && (
              <>
                <div
                  className="button-icon-preview"
                  dangerouslySetInnerHTML={{
                    __html: iconAsset.svg,
                  }}
                />
                <FontAwesomeIcon
                  className="button-icon-close-icon"
                  icon={faClose}
                  onClick={() =>
                    handleUpdateState(prev => ({
                      ...prev,
                      icon: null,
                    }))
                  }
                />
              </>
            )}
          </div>
          <div className="button-icon-action-container">
            <Button size="xsmall" block onClick={handleOpenSelectIconModal}>
              {t("Set Icon")}
            </Button>
          </div>
        </div>

        {updatedSettings.icon != null && (
          <Select
            label={t("Icon Position")}
            defaultValue={updatedSettings.icon.position}
            onChange={(position: TWebsiteBuilderButtonIconPosition) =>
              handleUpdateState(prev => ({
                ...prev,
                icon: prev.icon == null ? prev.icon : { ...prev.icon, position },
              }))
            }
            usePortal
            portalId={WEBSITE_BUILDER_PORTAL}
          >
            <Option value="left">{t("Left")}</Option>
            <Option value="right">{t("Right")}</Option>
          </Select>
        )}

        {updatedSettings.icon != null && (
          <Toggle
            labelRight={t("Show icon on hover")}
            checked={updatedSettings.icon.hoverMotion === "fade"}
            onChange={e =>
              handleUpdateState(prev => ({
                ...prev,
                icon: prev.icon == null ? prev.icon : { ...prev.icon, hoverMotion: e.target.checked ? "fade" : "none" },
              }))
            }
          />
        )}

        <WebsiteBuilderSelectIcon
          assetIdentity={updatedSettings.icon?.assetIdentity}
          isOpen={selectIconModalState.isOpen}
          closeModal={handleCloseSelectIconModal}
          handleSelectIcon={assetIdentity => {
            handleUpdateState(prev => ({
              ...prev,
              icon: {
                assetIdentity,
                position: prev.icon?.position ?? "left",
                hoverMotion: prev.icon?.hoverMotion ?? "none",
              },
            }));

            handleCloseSelectIconModal();
          }}
        />
      </>
    );
  }

  function renderLinkSettings() {
    return (
      <WebsiteBuilderOptionalLinkSettings
        link={updatedSettings.link}
        onChange={link =>
          handleUpdateState(prev => ({
            ...prev,
            link,
          }))
        }
        onDebouncedChange={link =>
          handleDebouncedUpdateState(prev => ({
            ...prev,
            link,
          }))
        }
      />
    );
  }

  function renderColorSettings() {
    return (
      <>
        <CustomColorPickerPopover
          title={t("Text")}
          hexColor={updatedSettings.color}
          updateHexColor={color =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              color,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Background")}
          hexColor={updatedSettings.backgroundColor}
          updateHexColor={backgroundColor =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              backgroundColor,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <CustomColorPickerPopover
          title={t("Border")}
          hexColor={updatedSettings.borderColor}
          updateHexColor={borderColor =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              borderColor,
            }))
          }
          portalParentId={WEBSITE_BUILDER_PORTAL}
        />
        <WebsiteBuilderHoverSettings>
          <CustomColorPickerPopover
            title={t("Text")}
            hexColor={updatedSettings.hoverColor}
            updateHexColor={hoverColor =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                hoverColor,
              }))
            }
            portalParentId={WEBSITE_BUILDER_PORTAL}
          />
          <CustomColorPickerPopover
            title={t("Background")}
            hexColor={updatedSettings.hoverBackgroundColor}
            updateHexColor={hoverBackgroundColor =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                hoverBackgroundColor,
              }))
            }
            portalParentId={WEBSITE_BUILDER_PORTAL}
          />
          <CustomColorPickerPopover
            title={t("Border")}
            hexColor={updatedSettings.hoverBorderColor}
            updateHexColor={hoverBorderColor =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                hoverBorderColor,
              }))
            }
            portalParentId={WEBSITE_BUILDER_PORTAL}
          />
        </WebsiteBuilderHoverSettings>
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Button style")}
      closePanel={closeSettings}
      sections={[renderGeneralSettings(), renderIconSettings(), renderLinkSettings(), renderColorSettings()]}
    />
  );
}
